import React from 'react';
import { ACE_ATTORNEY_ANSWER_MAX, formatString } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

export const AceAttorneyAnswers: React.FC = () => {
  const { socket, gameState, currentRoom, myAceAttorneyAnswer, myAceAttorneyJudgment, myPlayerId } = useSocket();
  const t = useTranslations();
  const copy = t.games.aceAttorney;
  const [answer, setAnswer] = React.useState('');
  const appliedAck = React.useRef(false);

  React.useEffect(() => {
    if (myAceAttorneyAnswer && !appliedAck.current) {
      setAnswer(myAceAttorneyAnswer);
      appliedAck.current = true;
    }
  }, [myAceAttorneyAnswer]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const setterId = gameState.aceAttorneySetterId;
  const isSetter = myPlayerId === setterId;
  const setter = currentRoom.players.find((player) => player.id === setterId);
  const answerers = currentRoom.players.filter((player) => !player.isSpectator);
  const submittedIds = new Set(gameState.aceAttorneySubmittedPlayerIds ?? []);
  const hasSubmitted = Boolean(myPlayerId && submittedIds.has(myPlayerId));

  const handleSubmit = () => {
    const trimmed = answer.trim();
    if (!socket || !trimmed) return;
    socket.emit('client:ace-attorney-submit-answer', trimmed);
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-4">
        <p className="text-xs sm:text-sm font-semibold text-amber-700">
          {formatString(copy.roundLabel, { round: String(currentRoom.currentRound || 1) })}
        </p>
        <p className="text-sm text-amber-800 mt-1">
          {copy.setterLabel}: {setter?.name ?? copy.setterLeft}
        </p>
      </div>

      <div className="mb-4 p-3 sm:p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
        <p className="text-xs sm:text-sm font-bold text-amber-800 mb-1">{copy.questionLabel}</p>
        <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">
          {gameState.aceAttorneyQuestion}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {isSetter && (
          <div className="p-3 sm:p-4 rounded-xl border-2 border-orange-200 bg-orange-50">
            <p className="text-xs sm:text-sm font-bold text-orange-800 mb-1">{copy.yourJudgment}</p>
            <p className="text-base text-gray-900 whitespace-pre-wrap break-words">
              {myAceAttorneyJudgment || '…'}
            </p>
          </div>
        )}
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent">
            {copy.answerTitle}
          </h2>
          <p className="text-sm sm:text-base text-gray-700 mt-2">
            {isSetter ? copy.setterWaiting : copy.answerInstruction}
          </p>
        </div>
        <textarea
          value={answer}
          onChange={(event) => setAnswer(event.target.value.slice(0, ACE_ATTORNEY_ANSWER_MAX))}
          rows={4}
          maxLength={ACE_ATTORNEY_ANSWER_MAX}
          placeholder={copy.answerPlaceholder}
          className="w-full p-3 border-2 border-amber-300 rounded-xl focus:outline-none focus:border-amber-500"
        />
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
          <span>
            {answer.length}/{ACE_ATTORNEY_ANSWER_MAX}
          </span>
          {hasSubmitted && <span className="text-green-700 font-semibold">{copy.submitted}</span>}
        </div>
        <button
          onClick={handleSubmit}
          disabled={answer.trim().length === 0}
          className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
        >
          {hasSubmitted ? copy.updateAnswer : copy.submitAnswer}
        </button>
      </div>

      <div className="mt-5 pt-4 border-t border-amber-100">
        <p className="text-xs sm:text-sm text-gray-600 mb-2 text-center">
          {formatString(copy.submittedProgress, {
            submitted: String(answerers.filter((player) => submittedIds.has(player.id)).length),
            total: String(answerers.length),
          })}
        </p>
        <ul className="flex flex-col gap-1">
          {answerers.map((player) => (
            <li key={player.id} className="flex items-center justify-between text-sm text-gray-800">
              <span className="truncate">{player.name}</span>
              <span className={submittedIds.has(player.id) ? 'text-green-700 font-semibold' : 'text-gray-400'}>
                {submittedIds.has(player.id) ? copy.submitted : copy.waiting}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
